import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, state } from "lit/decorators.js";
import type { RecordModel } from "pocketbase";
import {
  requestOtp,
  verifyOtp,
  listSsoProviders,
  signInWithSso,
  isSsoCancel,
  type SsoProvider,
} from "../api/auth.js";
import { parseError, errorMessage, humanError } from "../api/errors.js";
import { loginWithPasskey } from "../api/passkeys.js";
import {
  webauthnSupported,
  conditionalMediationAvailable,
  isWebAuthnCancel,
  passkeyFailureMessage,
} from "../api/webauthn.js";
import { pb } from "../api/client.js";
import { navigate } from "../nav.js";
import { toast } from "../components/index.js";

// A permissive check that only rejects obviously-malformed input; the server is
// the real authority. Trims first so trailing spaces don't fail the user.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * page-login is the passwordless sign-in: passkeys (WebAuthn) with the email
 * OTP flow as the universal fallback — signup is just first login. The OTP
 * path is two steps: enter an email to receive a one-time code (POST
 * request-otp), then enter that code to exchange it for a session token
 * (POST auth-with-otp), which the SDK persists in pb.authStore. Passkeys ride
 * two paths to the same end state: a background conditional-mediation
 * ceremony that surfaces saved passkeys in the email field's autofill, and an
 * explicit "Sign in with a passkey" button (the guaranteed path — conditional
 * UI availability varies by browser). All successes funnel through
 * finishAuth(): a fresh account (empty display name) detours to /welcome for
 * onboarding, everyone else returns to the `next` query param or /workspaces.
 * It renders inside the app shell's <main>; the shell already shows the brand
 * mark in the header.
 */
@customElement("page-login")
export class PageLogin extends LitElement {
  @state() private step: "email" | "code" = "email";
  @state() private email = "";
  @state() private code = "";
  @state() private otpId = "";
  @state() private submitting = false;
  /** In-flight flag for the explicit passkey button (separate from OTP submits). */
  @state() private passkeyBusy = false;
  /** Inline, field-level message shown under the active input. */
  @state() private fieldError = "";
  /** Configured SSO providers; empty until loaded, and stays empty when SSO is off. */
  @state() private ssoProviders: SsoProvider[] = [];
  /** Name of the provider whose popup is open, or "" when none is. */
  @state() private ssoBusy = "";

  /** Static per-browser capability; computed once so render stays cheap. */
  private readonly passkeySupport = webauthnSupported();

  /**
   * Aborts the background conditional-mediation ceremony. Browsers allow only
   * one pending WebAuthn request, so this MUST be aborted before any other
   * credentials.get() (the explicit button) and before leaving the page — a
   * dangling conditional request would otherwise block or leak.
   */
  private conditionalAbort?: AbortController;

  /**
   * When the current conditional ceremony was begun. The server forgets a
   * ceremony after 5 minutes (internal/passkeys ceremonyTTL) while the
   * browser's autofill offer lives on indefinitely — so a pick in a
   * long-idle tab would verify against a dead session. Refreshing on
   * visibility return (the common way a tab goes idle) keeps the ceremony
   * young; the failure re-arm below is the backstop for idling in the
   * foreground.
   */
  private conditionalStartedAt = 0;

  /**
   * Failure-driven re-arms this page-load. Each re-arm is triggered by a
   * user action (dismissing the prompt or a failed pick), so this can't
   * spin, but the cap keeps a pathological browser from looping begins.
   */
  private conditionalRearms = 0;

  private readonly onVisibility = () => {
    const stale = Date.now() - this.conditionalStartedAt > 4 * 60_000;
    if (document.visibilityState === "visible" && this.step === "email" && stale) {
      void this.startConditional();
    }
  };

  static override styles = css`
    :host {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding-top: clamp(var(--bino-space-lg), 8vh, 6rem);
    }
    .card {
      width: 100%;
      max-width: 25rem;
      box-sizing: border-box;
      background: var(--bino-surface);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius-lg);
      box-shadow: var(--bino-shadow-page);
      padding: var(--bino-space-xl);
    }
    .mark {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-lg);
    }
    .glyph {
      width: 2rem;
      height: 2rem;
      border-radius: var(--bino-radius-sm);
      background: var(--bino-primary);
      color: var(--bino-on-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: var(--bino-font-sans);
      font-weight: 800;
      font-size: var(--bino-font-size-md);
    }
    .wordmark {
      font-family: var(--bino-font-sans);
      font-weight: 600;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
    }
    h1 {
      margin: 0 0 var(--bino-space-xs);
      font-family: var(--bino-font-sans);
      font-size: 1.375rem;
      font-weight: 600;
      color: var(--bino-text);
      letter-spacing: -0.01em;
    }
    .lede {
      margin: 0 0 var(--bino-space-lg);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      line-height: 1.5;
      color: var(--bino-text-secondary);
    }
    .lede .addr {
      color: var(--bino-text);
      font-weight: 500;
      word-break: break-all;
    }
    .code-input {
      /* Space the digits out; overrides bn-input's inner font metrics via the
         host, which inherits into the shadowed <input>. */
      letter-spacing: 0.35em;
      font-size: var(--bino-font-size-md);
    }
    .actions {
      margin-top: var(--bino-space-lg);
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
    }
    .alt {
      margin-top: var(--bino-space-md);
      text-align: center;
    }
    /* SSO sits below the code/passkey actions behind a labelled rule, so the
       hierarchy reads: email code first, passkey, then company sign-in. */
    .sso {
      margin-top: var(--bino-space-md);
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
    }
    .sso-sep {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-xs);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .sso-sep::before,
    .sso-sep::after {
      content: "";
      flex: 1;
      height: 1px;
      background: var(--bino-border);
    }
    .link {
      appearance: none;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
      text-decoration: underline;
      text-underline-offset: 2px;
      border-radius: var(--bino-radius-sm);
      transition: color var(--bino-transition-fast);
    }
    .link:hover {
      color: var(--bino-text);
    }
    .link:focus-visible {
      outline: none;
      color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    // Already signed in — skip the form entirely.
    if (pb.authStore.isValid) {
      this.redirectNext();
      return;
    }
    document.addEventListener("visibilitychange", this.onVisibility);
    void this.startConditional();
    // Fire-and-forget: listSsoProviders never rejects, so a provider-list
    // hiccup leaves the email and passkey forms untouched instead of
    // stalling or erroring the page.
    void listSsoProviders().then((providers) => {
      if (this.isConnected) this.ssoProviders = providers;
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.conditionalAbort?.abort();
    this.conditionalAbort = undefined;
  }

  override updated(changed: PropertyValues) {
    if (changed.has("step")) {
      const sel = this.step === "email" ? "#email" : "#code";
      const field = this.renderRoot.querySelector(sel) as HTMLElement | null;
      // bn-input hosts the real <input> in its shadow root.
      (field?.shadowRoot?.querySelector("input") as HTMLInputElement | null)?.focus();
    }
  }

  private redirectNext() {
    const next = new URLSearchParams(location.search).get("next");
    navigate(next && next.startsWith("/") ? next : "/workspaces", { replace: true });
  }

  /**
   * finishAuth is the single post-sign-in funnel for every method (OTP,
   * conditional passkey, explicit passkey). An account without a display name
   * is a fresh signup — detour to /welcome for onboarding, carrying the
   * original destination along; everyone else goes straight there.
   */
  private finishAuth(record: RecordModel | null) {
    toast("Signed in", "success");
    const name = typeof record?.name === "string" ? record.name.trim() : "";
    // Only a genuinely fresh signup detours to onboarding: no display name
    // AND an account minted minutes ago (implicit OTP signup creates it when
    // the code is requested). Pre-existing accounts that simply never set a
    // name must go straight through — /welcome is a welcome, not a recurring
    // toll booth on every sign-in. (PocketBase timestamps use a space
    // separator; normalize to ISO for Date.parse.)
    const createdMs = record?.created ? Date.parse(String(record.created).replace(" ", "T")) : NaN;
    const fresh = Number.isFinite(createdMs) && Date.now() - createdMs < 15 * 60_000;
    if (name === "" && fresh) {
      const next = new URLSearchParams(location.search).get("next");
      const carry = next && next.startsWith("/") ? "?next=" + encodeURIComponent(next) : "";
      navigate("/welcome" + carry, { replace: true });
      return;
    }
    this.redirectNext();
  }

  /**
   * startConditional arms the browser's passkey autofill: a credentials.get()
   * with conditional mediation stays pending in the background and resolves
   * only when the user picks a passkey from the email field's dropdown.
   * Failures are deliberately quiet — this runs unprompted at page load, so a
   * begin-request hiccup or an unsupported browser must not toast at a user
   * who never asked for a passkey. The one exception: invalid_credential
   * means the user actively picked a passkey and verification failed, which
   * deserves feedback.
   */
  private async startConditional() {
    if (!this.passkeySupport || !(await conditionalMediationAvailable())) return;
    if (!this.isConnected) return; // page navigated away while we awaited
    this.conditionalAbort?.abort();
    const ac = new AbortController();
    this.conditionalAbort = ac;
    this.conditionalStartedAt = Date.now();
    try {
      const record = await loginWithPasskey({ conditional: true, signal: ac.signal });
      this.finishAuth(record);
    } catch (err) {
      if (ac.signal.aborted || !this.isConnected) return; // superseded or left the page
      // Non-cancel browser errors (SecurityError on an RP-ID/origin mismatch,
      // NotSupportedError) stay quiet here — this flow runs unprompted, and
      // re-arming would fail identically. The explicit button surfaces them.
      if (err instanceof DOMException && !isWebAuthnCancel(err)) return;
      // What's left is user-driven: they dismissed the prompt, or picked a
      // passkey and something server-side rejected it. Say so when it's a real
      // failure, and re-arm a FRESH ceremony either way — the resolved
      // credentials.get() is spent, so without a re-begin, passkey autofill
      // stays dead until a reload and the very next pick would fail too.
      //
      // passkeyFailureMessage, the same renderer the explicit button uses: it
      // returns null only for cancels. Matching on one code instead meant every
      // OTHER server rejection (a 500 from the finish route, a 429 from the
      // login-finish brake, a network drop) produced no toast, no field error
      // and no navigation — the user confirmed with Touch ID and watched
      // nothing at all happen.
      const msg = passkeyFailureMessage(err);
      if (msg) toast(msg, "error");
      if (this.conditionalRearms < 3) {
        this.conditionalRearms++;
        void this.startConditional();
      }
    }
  }

  /** The explicit "Sign in with a passkey" button — modal browser ceremony. */
  private async passkeySignIn() {
    if (this.submitting || this.passkeyBusy) return;
    // Only one WebAuthn request may be pending; kill the conditional one first.
    this.conditionalAbort?.abort();
    this.conditionalAbort = undefined;
    this.passkeyBusy = true;
    try {
      const record = await loginWithPasskey();
      this.finishAuth(record);
    } catch (err) {
      // Dismissing the browser dialog is a cancel — stay quiet. Everything
      // else (server rejection, RP-ID/origin mismatch, unsupported
      // authenticator) gets a toast; either way re-arm autofill below.
      const msg = passkeyFailureMessage(err);
      if (msg) toast(msg, "error");
      void this.startConditional();
    } finally {
      this.passkeyBusy = false;
    }
  }

  /**
   * The "Continue with <provider>" buttons — an OAuth2 popup against the
   * configured identity provider, landing in the same finishAuth() funnel as
   * every other method so onboarding and the `next` redirect behave
   * identically.
   */
  private async ssoSignIn(provider: SsoProvider) {
    if (this.submitting || this.passkeyBusy || this.ssoBusy !== "") return;
    // Same reason as the explicit passkey button: only one WebAuthn request
    // may be pending, and leaving the conditional one armed behind a popup
    // would block a later ceremony. Kill it; re-armed below on failure.
    this.conditionalAbort?.abort();
    this.conditionalAbort = undefined;
    this.ssoBusy = provider.name;
    try {
      const record = await signInWithSso(provider.name);
      this.finishAuth(record);
    } catch (err) {
      // Closing the provider window is a cancel — stay quiet, exactly as
      // dismissing the passkey dialog does. Real failures (provider rejection,
      // a blocked popup, a server error) toast.
      if (!isSsoCancel(err)) toast(humanError(err), "error");
      // Still on the email step, so restore the autofill we aborted above.
      void this.startConditional();
    } finally {
      this.ssoBusy = "";
    }
  }

  private failed(err: unknown) {
    const { code, message } = parseError(err);
    const msg = errorMessage(code, message);
    this.fieldError = msg;
    toast(msg, code === "rate_limited" ? "warning" : "error");
  }

  private async sendCode() {
    const email = this.email.trim();
    if (!EMAIL_RE.test(email)) {
      this.fieldError = "Enter a valid email address.";
      return;
    }
    this.fieldError = "";
    // Leaving the email step: the conditional ceremony's autofill target goes
    // away, so end it rather than leave a pending WebAuthn request behind.
    this.conditionalAbort?.abort();
    this.conditionalAbort = undefined;
    this.submitting = true;
    try {
      const { otpId } = await requestOtp(email);
      this.otpId = otpId;
      this.code = "";
      this.step = "code";
    } catch (err) {
      this.failed(err);
      // Still on the email step — re-arm the passkey autofill we aborted
      // above, or a failed send would leave it dead for the page's lifetime.
      void this.startConditional();
    } finally {
      this.submitting = false;
    }
  }

  private async verify() {
    const code = this.code.trim();
    if (code === "") {
      this.fieldError = "Enter the code from your email.";
      return;
    }
    this.fieldError = "";
    this.submitting = true;
    try {
      await verifyOtp(this.otpId, code);
      this.finishAuth(pb.authStore.record);
    } catch (err) {
      // No expired-vs-wrong branch: PocketBase returns the same opaque 400 for
      // both (see verifyOtp), so there is nothing to discriminate on. The user
      // stays on the code step, where "Resend code" and "Use a different
      // email" are already the two ways out.
      this.failed(err);
    } finally {
      this.submitting = false;
    }
  }

  private async resend() {
    this.fieldError = "";
    this.submitting = true;
    try {
      const { otpId } = await requestOtp(this.email.trim());
      this.otpId = otpId;
      this.code = "";
      toast("A new code is on its way.", "success");
    } catch (err) {
      this.failed(err);
    } finally {
      this.submitting = false;
    }
  }

  private backToEmail() {
    this.step = "email";
    this.otpId = "";
    this.code = "";
    this.fieldError = "";
    // The email field is back — re-arm passkey autofill for it.
    void this.startConditional();
  }

  private onKey(e: KeyboardEvent, action: () => void) {
    if (e.key === "Enter" && !this.submitting) {
      e.preventDefault();
      action();
    }
  }

  override render() {
    return html`
      <div class="card">
        <div class="mark">
          <span class="glyph" aria-hidden="true">b</span>
          <span class="wordmark">bino Cloud</span>
        </div>
        ${this.step === "email" ? this.renderEmail() : this.renderCode()}
      </div>
    `;
  }

  private renderEmail() {
    return html`
      <h1>Sign in</h1>
      <p class="lede">Enter your email and we'll send you a one-time code. No password needed.</p>
      <bn-field label="Email" ?required=${true} error=${this.fieldError}>
        <bn-input
          id="email"
          type="email"
          inputmode="email"
          autocomplete="email webauthn"
          placeholder="you@company.com"
          ?invalid=${this.fieldError !== ""}
          ?disabled=${this.submitting}
          .value=${this.email}
          @input=${(e: CustomEvent) => {
            this.email = (e.detail as { value: string }).value;
            if (this.fieldError) this.fieldError = "";
          }}
          @keydown=${(e: KeyboardEvent) => this.onKey(e, () => this.sendCode())}
        ></bn-input>
      </bn-field>
      <div class="actions">
        <bn-button
          variant="primary"
          block
          ?loading=${this.submitting}
          ?disabled=${this.ssoBusy !== ""}
          @click=${() => this.sendCode()}
          >Send code</bn-button
        >
        ${this.passkeySupport
          ? html`<bn-button
              variant="secondary"
              block
              ?loading=${this.passkeyBusy}
              ?disabled=${this.submitting || this.ssoBusy !== ""}
              @click=${() => void this.passkeySignIn()}
              >Sign in with a passkey</bn-button
            >`
          : nothing}
      </div>
      ${this.ssoProviders.length > 0
        ? html`<div class="sso">
            <div class="sso-sep">or</div>
            ${this.ssoProviders.map(
              (p) => html`<bn-button
                variant="secondary"
                block
                ?loading=${this.ssoBusy === p.name}
                ?disabled=${this.submitting ||
                this.passkeyBusy ||
                (this.ssoBusy !== "" && this.ssoBusy !== p.name)}
                @click=${() => void this.ssoSignIn(p)}
                >Continue with ${p.displayName}</bn-button
              >`,
            )}
          </div>`
        : nothing}
    `;
  }

  private renderCode() {
    return html`
      <h1>Check your email</h1>
      <p class="lede">
        We sent a one-time code to <span class="addr">${this.email.trim()}</span>. Enter it below to
        sign in.
      </p>
      <bn-field label="One-time code" ?required=${true} error=${this.fieldError}>
        <bn-input
          id="code"
          class="code-input"
          mono
          inputmode="numeric"
          autocomplete="one-time-code"
          placeholder="123456"
          ?invalid=${this.fieldError !== ""}
          ?disabled=${this.submitting}
          .value=${this.code}
          @input=${(e: CustomEvent) => {
            this.code = (e.detail as { value: string }).value;
            if (this.fieldError) this.fieldError = "";
          }}
          @keydown=${(e: KeyboardEvent) => this.onKey(e, () => this.verify())}
        ></bn-input>
      </bn-field>
      <div class="actions">
        <bn-button
          variant="primary"
          block
          ?loading=${this.submitting}
          @click=${() => this.verify()}
          >Sign in</bn-button
        >
      </div>
      <div class="alt">
        <button class="link" type="button" ?disabled=${this.submitting} @click=${() => this.resend()}>
          Resend code
        </button>
        &nbsp;·&nbsp;
        <button class="link" type="button" ?disabled=${this.submitting} @click=${() => this.backToEmail()}>
          Use a different email
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-login": PageLogin;
  }
}
